import React, { useState } from 'react';
import { CircularProgress, Typography, Button } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import dayjs from '../../../../../../../utils/dayjsConfig';
import PropTypes from 'prop-types';
import { Timestamp } from 'firebase/firestore';

import { getEventType } from '../../../../../../../utils/helpers/people/availabilityHelpers';
import DateTimePickerWrapper from '../../../../../../DateTimePickerWrapper/DateTimePickerWrapper';
import { generateICalendarRule } from '../../../../../../../redux/availability/availabilityService';
import { LOADING_STATES } from '../../../../../../../utils/constants/loadingStates';
import { selectSelectedEmployee } from '../../../../../../../redux/people/peopleSlice';
import { selectAvailabilityStatus } from '../../../../../../../redux/availability/availabilitySlice';
import { DAY_CODE_TO_INDEX_MAP } from '../../../../../../../utils/constants/dates';
import { selectCurrentVenue } from '../../../../../../../redux/slices/currentUserSlice';
import { getVenueTimezone } from '../../../../../../../utils/venueTimeFormatters';

import EventsDaysSelector from '../EventsDaysSelector/EventsDaysSelector';
import EventDatePickers from '../EventDatePickers/EventDatePickers';
import EventTimePickers from '../EventTimePickers/EventTimePickers';
import EventSwitches from '../EventSwitches/EventSwitches';

import {
  createAvailabilityEventThunk,
  updateAvailabilityEventThunk,
  deleteAvailabilityEventThunk,
} from '../../../../../../../redux/availability/availabilityThunks';

import {
  ContentBox,
  DrawerContentContainer,
  TitleBox,
  TitleTextField,
  ErrorMessage,
  ButtonContainer,
} from './EditEvent.styles';

const EditEvent = ({ isNewEvent = false, handleBackNavigation }) => {
  const dispatch = useDispatch();

  // SELECTORS
  const selectedEvent = useSelector((state) => state.availability.selectedEvent);
  const status = useSelector(selectAvailabilityStatus);
  const employeeId = useSelector(selectSelectedEmployee)?.id;
  const currentVenue = useSelector(selectCurrentVenue);
  const venueTimezone = getVenueTimezone(currentVenue);

  const {
    startDate: startDateString, // 'YYYY-MM-DD' (full day events only)
    endDate: endDateString, // 'YYYY-MM-DD' (full day events only)
    startTimeDate: startTimeDateString, // 'YYYY-MM-DD HH:mm'
    endTimeDate: endTimeDateString, // 'YYYY-MM-DD HH:mm'
    recurring,
    fullDays,
    days,
  } = selectedEvent || {};

  // Helper function to parse a date string in venue timezone and convert to UTC Date
  // DateTimePickerWrapper expects UTC Date objects and converts them back to venue timezone for display
  const parseVenueDateToUTC = (dateString, venueTz) => {
    if (!dateString) return null;
    const parsed = dayjs.tz(dateString, venueTz);
    return parsed.isValid() ? parsed.startOf('day').utc().toDate() : null;
  };

  // DEFAULT VALUES - Create default times in venue timezone, then convert to UTC
  const nowInVenue = dayjs().tz(venueTimezone);
  const fallbackDate = nowInVenue.startOf('day').utc().toDate();
  const defaultStartTime = nowInVenue.add(1, 'hour').startOf('hour').utc().toDate();
  const defaultEndTime = nowInVenue.add(2, 'hour').startOf('hour').utc().toDate();

  // Parse event times/dates from selected event
  // Prefer startTimeDate/endTimeDate for timed events, otherwise use startDate/endDate for full day events
  const getEventTimes = () => {
    if (isNewEvent || !selectedEvent) {
      return {
        startDate: fallbackDate,
        endDate: fallbackDate,
        startTime: defaultStartTime,
        endTime: defaultEndTime,
      };
    }

    // Parse start: prefer timed event, fallback to full day event
    let startDate = fallbackDate;
    let startTime = defaultStartTime;
    if (startTimeDateString) {
      const parsedInVenue = dayjs.tz(startTimeDateString, venueTimezone);
      if (parsedInVenue.isValid()) {
        startTime = parsedInVenue.utc().toDate();
        startDate = parsedInVenue.startOf('day').utc().toDate();
      }
    } else if (startDateString) {
      const parsed = parseVenueDateToUTC(startDateString, venueTimezone);
      if (parsed) startDate = parsed;
    }

    // Parse end: prefer timed event, fallback to full day event
    let endDate = fallbackDate;
    let endTime = defaultEndTime;
    if (endTimeDateString) {
      const parsedInVenue = dayjs.tz(endTimeDateString, venueTimezone);
      if (parsedInVenue.isValid()) {
        endTime = parsedInVenue.utc().toDate();
        endDate = parsedInVenue.startOf('day').utc().toDate();
      }
    } else if (endDateString) {
      const parsed = parseVenueDateToUTC(endDateString, venueTimezone);
      if (parsed) endDate = parsed;
    }

    return { startDate, endDate, startTime, endTime };
  };

  const {
    startDate: eventStartDate,
    endDate: eventEndDate,
    startTime: eventStartTime,
    endTime: eventEndTime,
  } = getEventTimes();

  const [title, setTitle] = useState(selectedEvent?.label || '');
  const [errorMessage, setErrorMessage] = useState('');
  const [isFullDays, setIsFullDays] = useState(fullDays);
  const [isRecurring, setIsRecurring] = useState(recurring);
  const [openStartPicker, setOpenStartPicker] = useState(false);
  const [openEndPicker, setOpenEndPicker] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(eventStartDate);
  const [selectedEndDate, setEndSelectedDate] = useState(eventEndDate);
  const [startTime, setStartTime] = useState(eventStartTime);
  const [endTime, setEndTime] = useState(eventEndTime);

  const [selectedDays, setSelectedDays] = useState(
    Array.isArray(days)
      ? days.map((day) => (typeof day === 'string' ? DAY_CODE_TO_INDEX_MAP[day] : day)).filter((d) => d !== undefined)
      : []
  );

  const isLoading = status === LOADING_STATES.LOADING;
  // singleDay events -> date, startTime, endTime
  // recurrent events -> recurring, startTime, endTime, days
  // fullDays events -> fullDays, startDate, endDate

  // HANDLERS
  const handleStartTimeChange = (newValue) => {
    setStartTime(newValue);
    setOpenStartPicker(false);
  };

  const handleStartDateChange = (newValue) => {
    setSelectedStartDate(newValue);
    setOpenStartPicker(false);
  };

  const handleEndTimeChange = (newValue) => {
    setEndTime(newValue);
    setOpenEndPicker(false);
  };

  const handleEndDateChange = (newValue) => {
    setEndSelectedDate(newValue);
    setOpenEndPicker(false);
  };

  const handleDateChange = (newValue) => {
    setSelectedStartDate(newValue);
    setOpenDatePicker(false);
  };

  const handleDayClick = (index) => {
    setSelectedDays((prev) => {
      if (prev.includes(index)) {
        return prev.filter((day) => day !== index);
      } else {
        return [...prev, index].sort((a, b) => a - b);
      }
    });
  };

  const handleRecurringChange = () => {
    setIsRecurring((prev) => {
      const newValue = !prev;
      // If turning on recurring, turn off full days
      if (newValue && isFullDays) {
        setIsFullDays(false);
      }
      return newValue;
    });
  };

  const handleFullDaysChange = () => {
    setIsFullDays((prev) => {
      const newValue = !prev;
      // If turning on full days, turn off recurring
      if (newValue && isRecurring) {
        setIsRecurring(false);
      }
      return newValue;
    });
  };

  const handleTitleChange = (e) => {
    const sanitizedValue = DOMPurify.sanitize(e.target.value);
    setTitle(sanitizedValue);
  };

  const handleCreateEvent = async (eventData) => {
    const rule = await generateICalendarRule(eventData);
    await dispatch(
      createAvailabilityEventThunk({
        eventData: {
          createdAt: Timestamp.now(),
          eid: employeeId,
          label: title,
          rule,
          type: getEventType(isRecurring, isFullDays),
          status: 'pending',
        },
      })
    ).unwrap();
  };

  const handleUpdateEvent = async (eventData) => {
    const rule = await generateICalendarRule(eventData);
    await dispatch(
      updateAvailabilityEventThunk({
        eventId: selectedEvent?.id,
        eventData: {
          updatedAt: Timestamp.now(),
          eid: selectedEvent?.eid || employeeId,
          label: title,
          rule,
          type: getEventType(isRecurring, isFullDays),
          status: 'pending',
        },
      })
    ).unwrap();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setErrorMessage('Please enter a title');
      return;
    }

    if (!isFullDays && startTime >= endTime) {
      setErrorMessage('Start time must be before end time');
      return;
    }
    if (isRecurring && selectedDays.length === 0) {
      setErrorMessage('Please select at least one day for a recurring event');
      return;
    }
    if (isFullDays && selectedStartDate > selectedEndDate) {
      setErrorMessage('End date must be after start date');
      return;
    }

    const eventData = {
      title,
      start: isFullDays ? selectedStartDate : startTime,
      end: isFullDays ? selectedEndDate : endTime,
      venueTimezone,
      date: isFullDays ? dayjs(selectedStartDate).startOf('day').toDate() : !isRecurring ? selectedStartDate : null,
      recurring: isRecurring,
      fullDays: isFullDays,
      repeatDays: isRecurring ? selectedDays : [],
    };

    try {
      if (isNewEvent) {
        await handleCreateEvent(eventData);
        handleBackNavigation();
      } else {
        await handleUpdateEvent(eventData);
        handleBackNavigation();
      }
    } catch (err) {
      console.error('Failed to save event', err);
      setErrorMessage('Failed to save event');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent?.id) return;

    try {
      const eventEmployeeId = selectedEvent?.eid || employeeId;
      await dispatch(deleteAvailabilityEventThunk({ userId: eventEmployeeId, eventId: selectedEvent.id })).unwrap();
      handleBackNavigation();
    } catch (err) {
      console.error('Failed to delete event', err);
      setErrorMessage('Failed to delete event');
    }
  };

  const normalizedSelectedDays = selectedDays.map((day) =>
    typeof day === 'string' ? DAY_CODE_TO_INDEX_MAP[day] : day
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}
    >
      <DrawerContentContainer alignment="center">
        <TitleBox>
          <Typography variant="bodyRegular">Label</Typography>
          <TitleTextField
            value={title}
            onChange={handleTitleChange}
            variant="outlined"
            placeholder="Enter event label"
            multiline
          />
        </TitleBox>
        <ContentBox>
          <EventSwitches
            isRecurring={isRecurring}
            handleRecurringChange={handleRecurringChange}
            isFullDays={isFullDays}
            handleFullDaysChange={handleFullDaysChange}
          />
        </ContentBox>
        <ContentBox>
          {/* SINGLE EVENT CASE */}
          {/*Show DATE picker and a TIME PICKERS if it is a single event*/}
          {!isRecurring && !isFullDays && (
            <>
              <DateTimePickerWrapper
                label="Date"
                value={selectedStartDate}
                size="large"
                open={openDatePicker}
                onOpen={() => setOpenDatePicker(true)}
                onClose={() => setOpenDatePicker(false)}
                onChange={handleDateChange}
                pickerType="date"
              />
              <EventTimePickers
                startTime={startTime}
                endTime={endTime}
                openStartPicker={openStartPicker}
                openEndPicker={openEndPicker}
                handleStartTimeChange={handleStartTimeChange}
                handleEndTimeChange={handleEndTimeChange}
                setOpenStartPicker={setOpenStartPicker}
                setOpenEndPicker={setOpenEndPicker}
              />
            </>
          )}

          {/* --IMPORTANT-- I did not reuse the code because the logic of each case is very difficult to understand,
          for that reason I created a separate logic to make it easier to understand */}

          {/* RECURRING EVENT CASE */}
          {/* Show TIME pickers for start and end */}
          {isRecurring && !isFullDays && (
            <EventTimePickers
              startTime={startTime}
              endTime={endTime}
              openStartPicker={openStartPicker}
              openEndPicker={openEndPicker}
              handleStartTimeChange={handleStartTimeChange}
              handleEndTimeChange={handleEndTimeChange}
              setOpenStartPicker={setOpenStartPicker}
              setOpenEndPicker={setOpenEndPicker}
            />
          )}
          {/* FULL DAYS EVENT CASE */}
          {/* Show DATE pickers for start and end */}
          {isFullDays && !isRecurring && (
            <EventDatePickers
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              openStartPicker={openStartPicker}
              openEndPicker={openEndPicker}
              handleStartDateChange={handleStartDateChange}
              handleEndDateChange={handleEndDateChange}
              setOpenStartPicker={setOpenStartPicker}
              setOpenEndPicker={setOpenEndPicker}
            />
          )}

          {/* RECURRENT FULL DAYS EVENT CASE */}
          {/* Show DATE pickers for start and end */}
          {isFullDays && isRecurring && (
            <EventDatePickers
              startDate={selectedStartDate}
              endDate={selectedEndDate}
              openStartPicker={openStartPicker}
              openEndPicker={openEndPicker}
              handleStartDateChange={handleStartDateChange}
              handleEndDateChange={handleEndDateChange}
              setOpenStartPicker={setOpenStartPicker}
              setOpenEndPicker={setOpenEndPicker}
            />
          )}
        </ContentBox>
        {isRecurring && (
          <EventsDaysSelector normalizedSelectedDays={normalizedSelectedDays} handleDayClick={handleDayClick} />
        )}
        {errorMessage && (
          <ErrorMessage>
            <Typography variant="bodyRegular">{errorMessage}</Typography>
          </ErrorMessage>
        )}
        <ButtonContainer>
          <Button variant="primary" onClick={handleSave}>
            {isLoading ? <CircularProgress color="inherit" size={16} /> : isNewEvent ? 'Create event' : 'Update event'}
          </Button>
          {!isNewEvent && (
            <Button variant="primaryDestructive" onClick={handleDelete}>
              Delete event
            </Button>
          )}
        </ButtonContainer>
      </DrawerContentContainer>
    </motion.div>
  );
};

EditEvent.propTypes = {
  isNewEvent: PropTypes.bool,
  handleBackNavigation: PropTypes.func.isRequired,
};

export default React.memo(EditEvent);
